"use client";

import React, { ReactNode } from 'react';
import Button from '@/components/stylizedButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { faMinus } from '@fortawesome/free-solid-svg-icons';

// Style Imports
import styles from "./accordionItem.module.scss";
import classNames from "classnames";

/**
 * A component to display an expanding and collapsing accordion.
 *
 * @param label - The text visible on the accordion button
 * @param id - The id for the button
 * @param onClick - The click event the sets the accordion button active
 * @param onKeyDown - The keydown event for accessibility
 * @param onKeyUp - The keyup event for accessibility
 * @param selected - A boolean value to toggle values within the button
 * @param variant - An option to determine if the style variant is primary or secondary
 */


interface AccordionItemProps {
  label: string;
  id: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  onKeyUp?: React.KeyboardEventHandler<HTMLButtonElement>;
  selected?: boolean;  // to determine if this accordion button is selected
  variant?: 'secondary' | 'primary',
  children: ReactNode;
}

const AccordionItem: React.FC<AccordionItemProps> = ({
  label,
  id,
  onClick,
  onKeyDown,
  onKeyUp,
  selected,
  variant = 'secondary',
  children,
}) => {
  const classes = [styles.accordionItem, styles.selected, styles[`${variant}`]];

  return (
    <div className={classNames(classes)}>
      {/* Accordion Button */}
      <div className={styles.accordionContainer}>
        <Button
          ariaPressed={selected}
          ariaControls={`${id}-accordion`}
          type="button"
          className={classNames(styles.accordionItemBtn, { [styles.selected] : selected })}
          tabIndex={0}
          id={id}
          onClick={onClick}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
        >
          <FontAwesomeIcon icon={ selected ? faMinus : faPlus } />
          {label}
        </Button>
      </div>

      <div
        id={`${id}-accordion`}
        className={classNames(styles.accordionPanel, styles.mobile, {[styles.selected]: selected })}
      >
        {children}
      </div>
    </div>
  );
};

export default AccordionItem;
